import { useRef, useState, type MouseEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** The server's flat ceiling for library uploads — checked here first so an
 * oversized file is rejected before any bytes leave the browser. */
const MAX_BYTES = 4 * 1024 * 1024 * 1024;

type Item = {
	file: File;
	status: "pending" | "uploading" | "done" | "error";
	error?: string;
};

function formatSize(bytes: number): string {
	if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
	if (bytes >= 1024 * 1024) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
	return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function UploadFilesModal({
	open,
	onClose,
	teamId,
}: {
	open: boolean;
	onClose: () => void;
	teamId?: string | null;
}) {
	const queryClient = useQueryClient();
	const inputRef = useRef<HTMLInputElement>(null);
	const [items, setItems] = useState<Item[]>([]);

	const uploadMutation = useMutation({
		mutationFn: async () => {
			const queue = items.filter((item) => item.status === "pending" && !item.error);

			// One at a time so a failure lands on the file that caused it.
			for (const item of queue) {
				setItems((prev) =>
					prev.map((i) => (i.file === item.file ? { ...i, status: "uploading" } : i)),
				);

				try {
					await api.uploadLibraryFile(item.file, teamId);
					setItems((prev) =>
						prev.map((i) => (i.file === item.file ? { ...i, status: "done" } : i)),
					);
				} catch (err) {
					// The endpoint is still being built — one toast, not one per file.
					if (err instanceof ApiError && err.code === "not_implemented") {
						toast.info("File uploads are coming soon.");
						setItems((prev) =>
							prev.map((i) =>
								i.status === "uploading" ? { ...i, status: "pending" } : i,
							),
						);
						return;
					}
					setItems((prev) =>
						prev.map((i) =>
							i.file === item.file
								? {
										...i,
										status: "error",
										error: err instanceof Error ? err.message : "Upload failed",
									}
								: i,
						),
					);
				}
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["files"] });
		},
	});

	function addFiles(files: File[]) {
		setItems((prev) => [
			...prev,
			...files.map((file) => ({
				file,
				status: "pending" as const,
				error: file.size > MAX_BYTES ? "Larger than 4 GB — the upload limit." : undefined,
			})),
		]);
	}

	function close() {
		if (uploadMutation.isPending) return;
		setItems([]);
		onClose();
	}

	const ready = items.filter((i) => i.status === "pending" && !i.error).length;

	return (
		<AnimatePresence>
			{open && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-scrim/40 backdrop-blur-sm"
					onClick={close}
				>
					<motion.div
						initial={{ scale: 0.95, opacity: 0, y: 20 }}
						animate={{ scale: 1, opacity: 1, y: 0 }}
						exit={{ scale: 0.95, opacity: 0, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
						className="relative w-full max-w-lg bg-bg-surface border border-border-subtle rounded-2xl shadow-popover flex flex-col max-h-[85vh]"
						onClick={(e: MouseEvent) => e.stopPropagation()}
					>
						<div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
							<h3 className="text-text-primary font-semibold">Upload files</h3>
							<Button
								variant="ghost"
								size="icon-sm"
								onClick={close}
								disabled={uploadMutation.isPending}
								className="text-text-tertiary"
							>
								<X size={18} />
							</Button>
						</div>

						<div className="px-6 py-5 overflow-y-auto space-y-4">
							<button
								onClick={() => inputRef.current?.click()}
								disabled={uploadMutation.isPending}
								className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl border border-dashed border-border-control text-text-secondary hover:bg-bg-sunken hover:text-text-primary transition-colors disabled:bg-action-disabled-bg disabled:text-action-disabled-fg disabled:cursor-not-allowed"
							>
								<UploadCloud size={22} />
								<span className="text-sm">Choose files</span>
							</button>

							<input
								ref={inputRef}
								type="file"
								multiple
								className="hidden"
								onChange={(e) => {
									addFiles(Array.from(e.target.files ?? []));
									e.target.value = "";
								}}
							/>

							{items.length > 0 && (
								<ul className="space-y-1.5">
									{items.map((item, index) => (
										<li
											key={`${item.file.name}-${index}`}
											className="flex items-start gap-3 text-sm px-3 py-2 rounded-lg bg-bg-canvas"
										>
											<div className="flex-1 min-w-0">
												<p className="text-text-primary break-words [overflow-wrap:anywhere]">{item.file.name}</p>
												<p
													className={`text-xs ${item.error ? "text-danger" : "text-text-tertiary"}`}
												>
													{item.error ?? formatSize(item.file.size)}
												</p>
											</div>
											<span className="text-xs text-text-tertiary shrink-0 pt-0.5">
												{item.status === "uploading" && (
													<Loader2 size={13} className="animate-spin" />
												)}
												{item.status === "done" && "✓"}
											</span>
										</li>
									))}
								</ul>
							)}
						</div>

						<div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border-subtle">
							<Button
								variant="ghost"
								onClick={close}
								disabled={uploadMutation.isPending}
							>
								Close
							</Button>
							<Button
								onClick={() => uploadMutation.mutate()}
								disabled={!ready || uploadMutation.isPending}
							>
								{uploadMutation.isPending && <Loader2 size={14} className="animate-spin" />}
								Upload{ready ? ` ${ready}` : ""}
							</Button>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
